<template>
  <v-container fluid class="free-listings-page pa-6">
    <v-row>
      <v-col cols="12">
        <v-sheet class="hero-panel pa-6" rounded="lg">
          <div class="hero-header">
            <div>
              <p class="hero-kicker mb-1">Admin Workspace</p>
              <h2 class="hero-title mb-2">Free Listings</h2>
              <p class="hero-subtitle mb-0">
                Review and manage all free listing records from one dashboard.
              </p>
            </div>
            <div class="hero-actions">
              <v-btn color="white" class="mr-3 hero-filter-btn" @click="toggleAdvanceSearch">
                <v-icon left>mdi-tune-variant</v-icon>
                {{ filtersOpen ? 'Hide Filters' : 'Show Filters' }}
              </v-btn>
              <v-btn
                v-if="modelPermissions.create"
                color="secondary"
                dark
                class="hero-create-btn"
                @click="openCreateDialog"
              >
                <v-icon left>mdi-plus</v-icon>
                Create Listing
              </v-btn>
            </div>
          </div>

          <div class="hero-metrics mt-5">
            <div class="metric-card">
              <p class="metric-label mb-1">Total Records</p>
              <p class="metric-value mb-0">{{ totalRecords }}</p>
            </div>
            <div class="metric-card">
              <p class="metric-label mb-1">Rows Loaded</p>
              <p class="metric-value mb-0">{{ loadedRecords }}</p>
            </div>
            <div class="metric-card">
              <p class="metric-label mb-1">Filters Active</p>
              <p class="metric-value mb-0">{{ hasActiveFilters ? 'Yes' : 'No' }}</p>
            </div>
          </div>
        </v-sheet>
      </v-col>
    </v-row>

    <v-row>
      <v-col cols="12">
        <v-expand-transition>
          <v-card v-show="filtersOpen" class="filters-card mb-5" outlined>
            <v-card-text class="pb-2">
              <v-row>
                <v-col cols="12" md="4">
                  <v-text-field
                    v-model.lazy="search"
                    clearable
                    clear-icon="mdi-close-circle"
                    prepend-inner-icon="mdi-magnify"
                    label="Search Name, Email or Phone"
                  ></v-text-field>
                </v-col>
                <v-col cols="12" md="4">
                  <v-text-field
                    v-model.lazy="email"
                    clearable
                    clear-icon="mdi-close-circle"
                    prepend-inner-icon="mdi-email-outline"
                    label="Filter by Email"
                  ></v-text-field>
                </v-col>
                <v-col cols="12" md="4" class="d-flex align-center justify-end">
                  <v-btn
                    outlined
                    color="primary"
                    :disabled="!hasActiveFilters"
                    @click="clearFilters"
                  >
                    <v-icon left>mdi-filter-remove</v-icon>
                    Clear Filters
                  </v-btn>
                </v-col>
              </v-row>
              <v-row>
                <v-col cols="12" md="6">
                  <select-categories
                    v-model="category"
                    label="Category"
                  ></select-categories>
                </v-col>
                <v-col cols="12" md="6">
                  <select-suburbs
                    v-model="suburb"
                    label="Suburb"
                  ></select-suburbs>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>
        </v-expand-transition>
      </v-col>
    </v-row>

    <v-row>
      <v-col cols="12">
        <v-card class="table-card" outlined>
          <v-data-table
            :server-items-length="free_listings.total"
            :loading="loading"
            :headers="headers"
            :items="free_listings.data"
            :options.sync="options"
            :items-per-page="5"
            class="free-listings-table"
            loading-text="Loading listings..."
          >
            <template v-slot:item.business_name="{ item }">
              <div class="name-cell">
                <v-avatar color="#e2e8f0" size="34">
                  <v-icon small color="#1e293b">mdi-storefront-outline</v-icon>
                </v-avatar>
                <div class="ml-3">
                  <p class="name-main mb-0">{{ item.business_name || '-' }}</p>
                  <p class="name-sub mb-0">Listing ID: {{ item.id }}</p>
                </div>
              </div>
            </template>

            <template v-slot:item.email="{ item }">
              <a
                v-if="item.email"
                :href="`mailto:${item.email}`"
                class="email-link"
              >
                <v-icon x-small class="mr-1">mdi-email</v-icon>
                {{ item.email }}
              </a>
              <span v-else class="grey--text text--darken-1">No email</span>
            </template>

            <template v-slot:item.text_messages_count="{ item }">
              <v-chip small color="#e0f2fe" text-color="#0c4a6e">
                {{ item.text_messages_count || 0 }} sent
              </v-chip>
            </template>

            <template v-slot:item.verified="{ item }">
              <v-chip
                small
                :color="item.verified === 1 ? '#dcfce7' : '#fee2e2'"
                :text-color="item.verified === 1 ? '#166534' : '#991b1b'"
              >
                {{ item.verified === 1 ? 'Verified' : 'Unverified' }}
              </v-chip>
            </template>

            <template v-slot:item.active="{ item }">
              <v-chip
                small
                :color="item.active === 1 ? '#dbeafe' : '#f3f4f6'"
                :text-color="item.active === 1 ? '#1e3a8a' : '#4b5563'"
              >
                {{ item.active === 1 ? 'Active' : 'Inactive' }}
              </v-chip>
            </template>

            <template v-if="modelPermissions.edit" v-slot:item.edit="{ item }">
              <v-btn icon color="primary" @click="editItem(item)">
                <v-icon small>mdi-pencil</v-icon>
              </v-btn>
            </template>

            <template v-if="modelPermissions.delete" v-slot:item.delete="{ item }">
              <v-btn icon color="error" @click="deleteItem(item)">
                <v-icon small>mdi-delete</v-icon>
              </v-btn>
            </template>

            <template v-slot:no-data>
              <div class="py-8 text-center">
                <v-icon color="grey" large class="mb-2">mdi-database-off-outline</v-icon>
                <p class="mb-0 grey--text text--darken-1">No free listings found.</p>
              </div>
            </template>
          </v-data-table>
        </v-card>
      </v-col>
    </v-row>

    <v-dialog persistent v-model="dialog">
      <free-listing-dialog
        v-if="dialog"
        :free_listings="free_listings"
        :item="item"
        :close="close"
      ></free-listing-dialog>
    </v-dialog>
  </v-container>
</template>

<script>
import FreeListingDialog from './FreeListingDialog'
import SelectSuburbs from '../../../components/SelectSuburbs'
import SelectCategories from '../../../components/SelectCategories'
import axios from '../../../utils/axios'
import { mapGetters } from 'vuex'

export default {
  name: 'FreeListingIndex',
  components: {
    FreeListingDialog,
    SelectCategories,
    SelectSuburbs
  },
  data() {
    return {
      filtersOpen: true,
      dialog: false,
      loading: false,
      headers: [
        {
          text: 'Name',
          align: 'start',
          value: 'business_name'
        },
        {
          text: 'Email',
          align: 'start',
          value: 'email'
        },
        {
          text: 'Messages Sent',
          align: 'start',
          value: 'text_messages_count'
        },
        {
          text: 'Verified',
          align: 'center',
          value: 'verified',
          sortable: false
        },
        { text: 'Status', align: 'center', value: 'active', sortable: false },
        { text: 'Edit', align: 'center', value: 'edit', sortable: false },
        { text: 'Delete', align: 'center', value: 'delete', sortable: false }
      ],
      free_listings: {
        data: [],
        total: 0
      },
      options: {},
      item: {},
      search: '',
      email: '',
      category: '',
      suburb: ''
    }
  },

  computed: {
    ...mapGetters(['permissions/getModelPermissions']),
    modelPermissions() {
      return this['permissions/getModelPermissions']('free_listing')
    },
    totalRecords() {
      return this.free_listings && this.free_listings.total
        ? this.free_listings.total
        : 0
    },
    loadedRecords() {
      return this.free_listings &&
        this.free_listings.data &&
        this.free_listings.data.length
        ? this.free_listings.data.length
        : 0
    },
    hasActiveFilters() {
      return (
        this.search !== '' ||
        this.email !== '' ||
        this.category !== '' ||
        this.suburb !== ''
      )
    }
  },
  methods: {
    toggleAdvanceSearch() {
      this.filtersOpen = !this.filtersOpen
    },
    clearFilters() {
      this.search = ''
      this.email = ''
      this.category = ''
      this.suburb = ''
    },
    openCreateDialog() {
      this.item = {}
      this.dialog = true
    },
    close() {
      this.item = {}
      this.dialog = false
    },
    editItem(item) {
      this.item = item
      this.dialog = true
    },
    getFreeListings() {
      const page = this.options.page || 1
      const itemsPerPage = this.options.itemsPerPage || 5
      this.loading = true
      axios
        .get(
          encodeURI(
            `api/admin/free-listings?page=${page}&itemsPerPage=${itemsPerPage}&search=${this.search}&email=${this.email}&category=${this.category}&suburb=${this.suburb}`
          )
        )
        .then(res => {
          this.free_listings = res.data
        })
        .catch(() => {
          this.free_listings = { data: [], total: 0 }
        })
        .finally(() => {
          this.loading = false
        })
    },
    deleteItem(item) {
      if (confirm(`Confirm Delete...${item.id}`)) {
        axios
          .delete(`api/admin/free-listings/${item.id}`)
          .then(res => {
            if (res.data === 1) {
              this.getFreeListings()
            }
          })
          .catch(() => {})
      }
    }
  },

  watch: {
    options: {
      handler() {
        this.getFreeListings()
      },
      deep: true
    },
    dialog: {
      handler() {
        this.getFreeListings()
      },
      deep: true
    },
    search: {
      handler() {
        this.getFreeListings()
      },
      deep: true
    },
    email: {
      handler() {
        this.getFreeListings()
      },
      deep: true
    },
    category: {
      handler() {
        this.getFreeListings()
      },
      deep: true
    },
    suburb: {
      handler() {
        this.getFreeListings()
      },
      deep: true
    }
  }
}
</script>

<style lang="scss" scoped>
.free-listings-page {
  background: linear-gradient(180deg, #f8fafc 0%, #eef2ff 100%);
  min-height: calc(100vh - 120px);
}

.hero-panel {
  background:
    radial-gradient(circle at 90% 10%, rgba(14, 165, 233, 0.3) 0, transparent 34%),
    linear-gradient(120deg, #0f172a 0%, #1e293b 50%, #334155 100%);
  color: #ffffff;
}

.hero-header {
  display: flex;
  justify-content: space-between;
  gap: 16px;
  align-items: flex-start;
  flex-wrap: wrap;
}

.hero-kicker {
  letter-spacing: 0.08em;
  text-transform: uppercase;
  font-size: 0.72rem;
  color: rgba(255, 255, 255, 0.78);
}

.hero-title {
  font-size: 2rem;
  line-height: 1.1;
}

.hero-subtitle {
  color: rgba(255, 255, 255, 0.82);
  max-width: 620px;
}

.hero-actions {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
}

.hero-filter-btn {
  color: #0f172a;
  font-weight: 600;
}

.hero-create-btn {
  font-weight: 600;
}

.hero-metrics {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 12px;
}

.metric-card {
  background: rgba(255, 255, 255, 0.12);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 12px;
  padding: 12px;
}

.metric-label {
  font-size: 0.72rem;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: rgba(255, 255, 255, 0.8);
}

.metric-value {
  font-size: 1.45rem;
  font-weight: 700;
}

.filters-card,
.table-card {
  border-color: #dbe4f0 !important;
  border-radius: 14px;
}

.free-listings-table ::v-deep thead tr th {
  font-weight: 700 !important;
  color: #334155 !important;
  background: #f8fafc;
}

.name-cell {
  display: flex;
  align-items: center;
}

.name-main {
  font-weight: 600;
  color: #0f172a;
}

.name-sub {
  color: #64748b;
  font-size: 0.72rem;
}

.email-link {
  color: #0c4a6e;
  text-decoration: none;
  font-weight: 500;
}

.email-link:hover {
  text-decoration: underline;
}

@media (max-width: 960px) {
  .free-listings-page {
    padding: 16px !important;
  }

  .hero-title {
    font-size: 1.6rem;
  }
}
</style>
